import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import LockScreen from "~/components/LockScreen";
import { useLoader } from "~/contexts/LoaderContext";
import { ShieldAlert } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_URL;

function SecurityRoute({ children }) {
    const location = useLocation();
    const [isLocked, setIsLocked] = useState(false);
    const [isProtectedAndUnlocked, setIsProtectedAndUnlocked] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const { showLoader, hideLoader } = useLoader();

    useEffect(() => {
        const checkSecurity = async () => {
            setIsChecking(true);
            try {
                // Fetch locked routes
                const res = await axios.get(`${apiUrl}/api/admin/security/locked-routes`);
                if (res.data?.Succeeded) {
                    const lockedRoutes = res.data.Data || [];
                    const currentPath = location.pathname.toLowerCase();
                    
                    // Check if current path matches any locked route
                    const requiresAuth = lockedRoutes.some(route => currentPath.includes(route.toLowerCase()));
                    const isUnlocked = sessionStorage.getItem('superAdminUnlocked') === 'true';

                    if (requiresAuth) {
                        if (isUnlocked) {
                            setIsLocked(false);
                            setIsProtectedAndUnlocked(true);
                        } else {
                            setIsLocked(true);
                            setIsProtectedAndUnlocked(false);
                        }
                    } else {
                        setIsLocked(false);
                        setIsProtectedAndUnlocked(false);
                    }
                }
            } catch (error) {
                console.error("Error checking security routes", error);
                setIsLocked(false);
                setIsProtectedAndUnlocked(false);
            } finally {
                setIsChecking(false);
            }
        };

        checkSecurity();
    }, [location.pathname]);

    useEffect(() => {
        if (isChecking) {
            showLoader();
            return () => hideLoader();
        }
    }, [isChecking, showLoader, hideLoader]);

    if (isChecking) {
        return null;
    }

    if (isLocked) {
        return <LockScreen onUnlock={() => { setIsLocked(false); setIsProtectedAndUnlocked(true); }} />;
    }

    return (
        <div className="d-flex flex-column h-100 w-100">
            {isProtectedAndUnlocked && (
                <div className="security-banner bg-warning bg-opacity-10 text-warning-emphasis px-4 py-2 d-flex align-items-center justify-content-between fw-medium border-bottom border-warning border-opacity-25 fs-9" style={{ marginTop: '-2rem', marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '2rem', zIndex: 100 }}>
                    <style>
                        {`
                            @media (min-width: 768px) {
                                .security-banner {
                                    margin-left: -2.5rem !important;
                                    margin-right: -2.5rem !important;
                                }
                            }
                        `}
                    </style>
                    <div className="d-flex align-items-center">
                        <ShieldAlert size={18} className="me-2 flex-shrink-0" />
                        <span>Security Notice: You are viewing a protected area. Super Admin privileges are active. <strong>Please lock the session when you are finished to protect this area.</strong></span>
                    </div>
                    <button 
                        className="btn btn-sm btn-outline-warning text-warning-emphasis border-warning-subtle d-flex align-items-center fw-bold py-1 px-3 ms-3 flex-shrink-0"
                        onClick={() => {
                            sessionStorage.removeItem('superAdminUnlocked');
                            setIsLocked(true);
                            setIsProtectedAndUnlocked(false);
                        }}
                        title="Lock this session immediately"
                    >
                        Lock Now
                    </button>
                </div>
            )}
            <div className="flex-grow-1 position-relative">
                {children}
            </div>
        </div>
    );
}

export default SecurityRoute;
